import React, {
  useContext,
  FC,
  useEffect,
  useState,
  useRef,
  useCallback
} from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import {
  getActiveConsultations,
  getActiveConsultationsLoadingSelector,
  getConsultationsRecordCountSelector,
  getConsultationsRecordSelector
} from 'store/consultationsQueue/consultationsQueue.redux';
import {
  PeopleIcon,
  SettingsIcon,
  CloseIcon,
  ArrowRightIcon
} from 'shared/components/Icons';
import { Text } from 'shared/components/Text';
import { Spinner } from 'shared/components/Spinner';
import { OrderStatuses } from 'shared/enums';
import { Sidebar, SidebarContext } from '../../';
import { Card } from './Card';
import classes from './styles.module.scss';
import { classNames } from 'shared/utils/class-names';

const Content = () => {
  const dispatch = useDispatch();
  const records = useSelector(getConsultationsRecordSelector);
  const isLoading = useSelector(getActiveConsultationsLoadingSelector);
  const countRecords = useSelector(getConsultationsRecordCountSelector);
  const { onClose } = useContext(SidebarContext);
  const [currentPage, setCurrentPage] = useState(1);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    dispatch(
      getActiveConsultations({
        page: currentPage,
        orderStatus: OrderStatuses.RequiresProviderReview
      })
    );
  }, [currentPage, dispatch]);

  const lastElementRef = useCallback(
    (nodeElem: Element | null): HTMLDivElement | undefined => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (records.length !== countRecords) {
            setCurrentPage((prev) => prev + 1);
          }
        }
      });
      if (nodeElem) observer.current.observe(nodeElem);
    },
    [countRecords, isLoading, records.length]
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.top}>
        <div className={classes.top__actions}>
          <Text type="sf-subheading-light" color="grey" upperCase>
            Most Recent
          </Text>
          <div>
            {/* <span> */}
            {/*  <SettingsIcon /> */}
            {/* </span> */}
            {onClose && (
              <span onClick={onClose}>
                <CloseIcon />
              </span>
            )}
          </div>
        </div>
        <Text type="m-titles-bold" color="blue-ultra-dark">
          Consultations Queue
        </Text>
        {/* <Text type="sf-body" color="grey"> */}
        {/*  Secondary information */}
        {/* </Text> */}
      </div>
      <div className={classes.content}>
        {isLoading && (
          <div className={classes['content-spinner']}>
            <div className={classes.spinner}>
              <Spinner />
            </div>
          </div>
        )}
        <div
          className={classNames({ [classes['content-loading']]: isLoading })}
        >
          {records.map((item, i) => {
            if (records.length === i + 1) {
              return (
                <div ref={lastElementRef} key={i}>
                  <Card {...item} />
                </div>
              );
            } else {
              return <Card key={i} {...item} />;
            }
          })}
        </div>
      </div>
      <div className={classes.footer}>
        <Link to="/consultations">
          <Text component="span" type="sf-body-medium">
            Consultations Queue
          </Text>
          <div className={classes['footer-icon']}>
            <ArrowRightIcon />
          </div>
        </Link>
      </div>
    </div>
  );
};

interface IConsultationQueue {
  notClosable?: boolean;
}

export const ConsultationQueue: FC<IConsultationQueue> = ({
  notClosable = false
}) => {
  return (
    <Sidebar icon={<PeopleIcon />} notClosable={notClosable}>
      <Content />
    </Sidebar>
  );
};
